/**
 * @file    usePartitionsManager.ts
 * @scope   VIEW
 * @summary Orchestration hook for the PartitionsManager view: data-source CRUD, drag-and-drop
 *          reorder, auto-selection and per-partition field-count display + validation.
 * @consumes DataSource/FieldsManager models, partition validation, usePartitionStates
 * @produces State, handlers and derived data the PartitionsManager component renders
 * @sideEffects None beyond local state; notifies the caller via `onChange` when the list changes.
 * @notes Keep the JSX in PartitionsManager.tsx declarative; the logic lives here.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { arrayMove } from 'helpers/sorting';
import { PartitionModel, DataSourceItem } from 'models/Enterprise/DataSourceModel';
import { SelectedUserFields } from 'models/Enterprise/FieldsManagerModel';
import {
  getDataSourceFieldCount,
  isDataSourceItemInvalid,
} from '../../utils/validation/partitionValidation';
import { usePartitionStates } from 'hooks/Enterprise/usePartitionState';
import { useTouchedItems } from '../../../../common/hooks/useTouchedItems';
import { useDragReorder } from '../../../../common/hooks/useDragReorder';

/** Parameters for the usePartitionsManager hook. */
interface UsePartitionsManagerParams {
  /** All partition options available for selection. */
  availableOptions: PartitionModel[];
  /** Pre-selected partitions (edit mode). */
  selectedValues?: PartitionModel[];
  /** Callback invoked whenever the data source list changes. */
  onChange?: (dataSources: DataSourceItem[]) => void;
  /** Configured field groups keyed by partition — used for field-count display and validation. */
  selectedEnterpriseFields?: SelectedUserFields[];
}

/**
 * Manages state, effects and handlers for the PartitionsManager view.
 * Handles data-source CRUD, drag-and-drop reorder, auto-selection,
 * edit-mode population and per-item validation state.
 */
export const usePartitionsManager = ({
  availableOptions,
  selectedValues,
  onChange,
  selectedEnterpriseFields,
}: UsePartitionsManagerParams) => {
  const [dataSources, setDataSources] = useState<DataSourceItem[]>(
    selectedValues && selectedValues.length > 0
      ? selectedValues.map((val, index) => ({
          id: `ds-${index}`,
          selectedValue: val,
        }))
      : []
  );
  // Pre-existing items (edit mode) start as touched.
  const {
    touchedIds: touchedItemIds,
    isListTouched: touchedDataSources,
    markTouched,
    touchList,
  } = useTouchedItems(dataSources.map((s) => s.id));
  const hasPopulatedFromProps = useRef(false);
  const hasAutoSelected = useRef(false);

  // Auto-select when only one partition is available
  useEffect(() => {
    if (hasAutoSelected.current || dataSources.length > 0) return;
    if (availableOptions?.length === 1) {
      hasAutoSelected.current = true;
      handleAddDataSource(availableOptions[0]);
    }
  }, [availableOptions]);

  // Populate from props in edit mode (once)
  useEffect(() => {
    if (hasPopulatedFromProps.current) return;
    if (!availableOptions?.length || !selectedEnterpriseFields?.length) return;

    hasPopulatedFromProps.current = true;
    const populated = selectedEnterpriseFields.reduce<DataSourceItem[]>((acc, field, index) => {
      const match = availableOptions.find((o) => o.value === field.partitionGuid) ?? null;
      if (match !== null) acc.push({ id: `ds-${index}`, selectedValue: match });
      return acc;
    }, []);

    if (populated.length > 0) {
      setDataSources(populated);
    }
  }, [availableOptions, selectedEnterpriseFields]);

  /** Marks every current data source as touched for validation purposes. */
  const markAllTouched = useCallback(
    () => markTouched(dataSources.map((s) => s.id)),
    [dataSources, markTouched]
  );

  /**
   * Appends a new partition to the data source list.
   * @param value - The partition to add (no-op if null)
   */
  const handleAddDataSource = useCallback(
    (value: PartitionModel | null) => {
      if (!value) return;
      const newId = `ds-${Date.now()}`;
      const updated = [...dataSources, { id: newId, selectedValue: value }];
      markAllTouched();
      setDataSources(updated);
      onChange?.(updated);
    },
    [dataSources, markAllTouched, onChange]
  );

  /**
   * Removes a data source by its unique id.
   * @param id - The id of the data source item to remove
   */
  const handleRemoveDataSource = useCallback(
    (id: string) => {
      const updated = dataSources.filter((item) => item.id !== id);
      setDataSources(updated);
      onChange?.(updated);
      touchList();
    },
    [dataSources, onChange, touchList]
  );

  /** Moves a data source to a new position (drag-and-drop reorder). */
  const moveDataSource = useCallback(
    (from: number, to: number) => {
      const updated = arrayMove(dataSources, from, to);
      markAllTouched();
      setDataSources(updated);
      onChange?.(updated);
    },
    [dataSources, markAllTouched, onChange]
  );

  const getIndexById = useCallback(
    (id: string) => dataSources.findIndex((s) => s.id === id),
    [dataSources]
  );

  const { handleDragStart, handleDragOver, handleDrop } = useDragReorder({
    getIndexById,
    onMove: moveDataSource,
  });

  /** Marks the adder select as touched when it loses focus. */
  const handleBlurAdder = touchList;

  /** Returns partition options not yet selected, for the adder dropdown. */
  const getAvailableOptionsForAdd = useCallback(() => {
    const selected = dataSources.map((item) => item.selectedValue?.value).filter(Boolean);
    const base = availableOptions || selected;
    return base.filter((option) => !selected.includes(option.value));
  }, [dataSources, availableOptions]);

  /**
   * Returns a human-readable field count label for a given partition.
   * @param selectedValue - The partition to look up
   * @returns Display string (e.g. '2 fields', 'No fields')
   */
  const getFieldCountForDataSource = useCallback(
    (selectedValue: PartitionModel | null): string =>
      getDataSourceFieldCount(selectedValue, selectedEnterpriseFields || []),
    [selectedEnterpriseFields]
  );

  /**
   * Checks whether a data source item should display as invalid.
   * @param source - The data source item to validate
   * @returns true if the item is touched and has no configured fields
   */
  const isItemInvalid = useCallback(
    (source: DataSourceItem): boolean =>
      isDataSourceItemInvalid(source, touchedItemIds, selectedEnterpriseFields || []),
    [touchedItemIds, selectedEnterpriseFields]
  );

  const showEmptyError = touchedDataSources && dataSources.length === 0;

  const { globalState, itemStates: partitionStates } = usePartitionStates(
    dataSources,
    availableOptions
  );

  return {
    dataSources,
    globalState,
    partitionStates,
    showEmptyError,
    handleAddDataSource,
    handleRemoveDataSource,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleBlurAdder,
    getAvailableOptionsForAdd,
    getFieldCountForDataSource,
    isItemInvalid,
  };
};
